import React, { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Ship, Globe2, Waves, Thermometer, NotepadText, Activity, BarChart3, Compass, CloudRain, Mountain, ChevronLeft, ChevronRight } from 'lucide-react';
import { Switch } from '../components/ui/switch';
import { Button } from '../components/ui/button';
import { cn } from '../lib/utils';

const Sidebar = ({ layers, toggleLayer, currentRoute }) => {
  const location = useLocation();
  const activeRoute = currentRoute || location.pathname;
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);

  const navItems = [
    { icon: Ship, label: 'Mission Control', route: '/' },
    { icon: Globe2, label: '3D Globe', route: '/globe' },
    { icon: Waves, label: 'Sonar', route: '/sonar' },
    { icon: Thermometer, label: 'Ocean Layers', route: '/layers' },
    { icon: NotepadText, label: 'Crew Logs', route: '/logs' },
    { icon: Activity, label: 'Alerts', route: '/alerts' },
    { icon: BarChart3, label: 'Analytics', route: '/analytics' },
    { icon: Compass, label: 'Mission Planning', route: '/missions' },
    { icon: CloudRain, label: 'Weather Forecast', route: '/weather' },
    { icon: Mountain, label: 'Bathymetry', route: '/bathymetry' },
  ];

  const layerItems = [
    { id: 'depth', label: 'Depth (bathymetry)', color: 'text-accent' },
    { id: 'temperature', label: 'Temperature', color: 'text-chart-1' },
    { id: 'salinity', label: 'Salinity', color: 'text-chart-3' },
  ];

  return (
    <>
      {/* Mobile Overlay */}
      {isMobileOpen && (
        <div 
          className="fixed inset-0 bg-black/60 backdrop-blur-sm z-40 lg:hidden"
          onClick={() => setIsMobileOpen(false)}
          data-testid="sidebar-overlay"
        />
      )}

      {/* Mobile Menu Button */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsMobileOpen(!isMobileOpen)}
        className="fixed top-4 left-4 z-50 lg:hidden h-10 w-10 rounded-md border border-border/60 bg-background/90 backdrop-blur-sm shadow-lg hover:bg-secondary"
        data-testid="mobile-menu-toggle"
        aria-label="Toggle menu"
      >
        <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
        </svg>
      </Button>

      <aside 
        className={cn(
          "lg:col-start-1 lg:row-start-1 lg:row-end-3 relative lg:sticky left-0 top-0 h-screen border-r border-border/60 bg-secondary/90 backdrop-blur-sm overflow-y-auto scrollbar-thin scrollbar-thumb-primary/20 scrollbar-track-transparent z-50 transition-all duration-300",
          // Desktop behavior
          isCollapsed ? "lg:w-20" : "lg:w-[280px]",
          // Mobile behavior
          "fixed w-[280px]",
          isMobileOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
      {/* Toggle Button - Only visible on desktop */}
      <Button
        variant="ghost"
        size="icon"
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="absolute -right-3 top-6 z-[60] hidden lg:flex h-7 w-7 rounded-full border-2 border-primary/40 bg-background shadow-lg hover:bg-secondary hover:border-primary/60 transition-all"
        data-testid="sidebar-toggle"
        aria-label={isCollapsed ? "Expand sidebar" : "Collapse sidebar"}
      >
        {isCollapsed ? (
          <ChevronRight className="h-4 w-4 text-primary" />
        ) : (
          <ChevronLeft className="h-4 w-4 text-primary" />
        )}
      </Button>

      <div className={cn("p-6 pb-24 transition-all duration-300", isCollapsed && "px-3")}>
        {/* Logo */}
        <div className="mb-8" data-testid="sidebar-logo">
          {!isCollapsed ? (
            <>
              <h1 className="text-xl font-semibold tracking-tight text-primary">PELAGIA OPS</h1>
              <p className="text-xs text-muted-foreground mt-1">Ocean research, rendered in real time</p>
            </>
          ) : (
            <h1 className="text-lg font-bold tracking-tight text-primary text-center">PO</h1>
          )}
        </div>

        {/* Navigation */}
        <div className="mb-6">
          {!isCollapsed && (
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-3 px-3">Navigation</h3>
          )}
          <nav className="space-y-0.5" data-testid="sidebar-nav">
            {navItems.map((item, idx) => {
              const Icon = item.icon;
              const isActive = activeRoute === item.route;
              return (
                <Link
                  key={idx}
                  to={item.route}
                  onClick={() => setIsMobileOpen(false)}
                  data-testid={`nav-item-${item.label.toLowerCase().replace(/\s+/g, '-')}`}
                  title={isCollapsed ? item.label : undefined}
                  className={cn(
                    "w-full flex items-center gap-3 px-3 py-2 rounded-md text-sm transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
                    isActive
                      ? "bg-primary/10 text-primary font-medium"
                      : "text-muted-foreground hover:bg-white/5 hover:text-foreground",
                    isCollapsed && "justify-center px-0"
                  )}
                >
                  <Icon className="h-4 w-4 flex-shrink-0" />
                  {!isCollapsed && <span className="truncate">{item.label}</span>}
                </Link>
              );
            })}
          </nav>
        </div>

        {/* Layer Toggles */}
        {!isCollapsed && (
          <div className="mb-6">
            <h3 className="text-xs uppercase tracking-wide text-muted-foreground mb-3 px-3">Data Layers</h3>
            <div className="space-y-2" data-testid="layer-toggles">
              {layerItems.map((item) => (
                <div
                  key={item.id}
                  className="flex items-center justify-between px-3 py-2"
                  data-testid={`layer-toggle-${item.id}`}
                >
                  <label htmlFor={item.id} className={cn("text-sm cursor-pointer", item.color)}>
                    {item.label}
                  </label>
                  <Switch
                    id={item.id}
                    checked={layers[item.id]}
                    onCheckedChange={() => toggleLayer(item.id)}
                    data-testid={`layer-switch-${item.id}`}
                  />
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Status Footer */}
        {!isCollapsed && (
          <div className="mt-6 pt-6 border-t border-border/40">
            <div className="px-3 space-y-2 text-xs text-muted-foreground">
              <div className="flex items-center justify-between">
                <span>System Status</span>
                <span className="flex items-center gap-1.5">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                  <span className="text-emerald-400">Operational</span>
                </span>
              </div>
              <div className="flex items-center justify-between">
                <span>Build</span>
                <span>1.0.0</span>
              </div>
            </div>
          </div>
        )}
      </div>
    </aside>
    </>
  );
};

export default Sidebar;
